import { Injectable } from '@angular/core';
import { TimeEntry } from '../api/models';
import {
  AlertController,
  LoadingController,
  Platform,
  ToastController,
} from '@ionic/angular';
import { DatePipe } from '@angular/common';
import { Browser } from '@capacitor/browser';

@Injectable({
  providedIn: 'root',
})
export class ToolService {
  constructor(
    private toastController: ToastController,
    private alertController: AlertController,
    private loadingController: LoadingController,
    public datepipe: DatePipe,
    public platform: Platform
  ) {}

  options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  public toastButtons = [
    {
      text: 'Okay',
      role: 'cancel',
    },
  ];

  formatDt(dt: string, showYear: boolean = true): string {
    if (dt == null) return '-';
    const da = new Date(dt);
    if (da.getUTCFullYear() < 1000) {
      return '-';
    }
    return (
      ('0' + da.getUTCDate()).slice(-2) +
      '.' +
      ('0' + (da.getUTCMonth() + 1)).slice(-2) +
      '.' +
      (showYear ? da.getUTCFullYear().toString() : '')
    );
  }

  formatTime(dt: string, addTimeZone: boolean = false): string {
    let fixDate = new Date(dt);
    if (addTimeZone) {
      fixDate.setMinutes(
        fixDate.getMinutes() + Math.abs(fixDate.getTimezoneOffset())
      );
    }

    if (fixDate.getUTCFullYear() < 1000) {
      return '';
    }
    return (
      ('0' + fixDate.getUTCHours()).slice(-2) +
      ':' +
      ('0' + fixDate.getUTCMinutes()).slice(-2) +
      ' Uhr'
    );
  }

  addOneHour(isoTime: string): string {
    const date = new Date(isoTime);
    date.setHours(date.getHours() + 1);
    return date.toISOString();
  }

  timeEntriesInvalid(entries: TimeEntry[]): boolean {
    return (
      entries.find(
        (x) =>
          new Date(x.start).getUTCHours() > new Date(x.end).getUTCHours() ||
          (new Date(x.start).getUTCHours() == new Date(x.end).getUTCHours() &&
            new Date(x.start).getUTCMinutes() >=
              new Date(x.end).getUTCMinutes())
      ) != null
    );
  }

  async showErrorToast(err: string) {
    const toast = await this.toastController.create({
      message: err,
      duration: 7000,
      position: 'bottom',
      color: 'danger',
      buttons: this.toastButtons,
    });

    await toast.present();
  }

  async showWarningToast(err: string) {
    const toast = await this.toastController.create({
      message: err,
      duration: 7000,
      position: 'bottom',
      color: 'warning',
      buttons: this.toastButtons,
    });

    await toast.present();
  }

  async showInfoToast(msg: string) {
    const toast = await this.toastController.create({
      message: msg,
      duration: 3000,
      position: 'bottom',
      color: 'primary',
    });

    await toast.present();
  }

  async showUpdateAvailableToast() {
    const toast = await this.toastController.create({
      message: 'Es ist eine neue App Version verfügbar!',
      duration: 7000,
      position: 'bottom',
      color: 'primary',
      buttons: [
        {
          text: 'Update',
          role: 'cancel',
          handler: () => {
            this.openAppStore();
          },
        },
      ],
    });

    await toast.present();
  }

  async showBasicAlert(
    message: string,
    header: string,
    option1: string,
    option2: string
  ): Promise<string> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header,
        message,
        backdropDismiss: true,
        buttons: [
          {
            text: option1,
            handler: () => resolve(option1),
            role: 'option1',
          },
          {
            text: option2,
            handler: () => resolve(option2),
            role: 'option2',
          },
        ],
      });
      await alert.present();

      const { role } = await alert.onDidDismiss();

      if (role === 'backdrop') {
        resolve('backdrop');
      }
    });
  }

  async showConfirmAlert(
    isDelete: boolean,
    message: string,
    header: string = ''
  ): Promise<boolean> {
    if (isDelete) return await this.showDeleteAlert(message);
    return await this.showDeleteAlert('', message, header, 'Ja');
  }

  async showDeleteAlert(
    itemName: string,
    message?: string,
    header?: string,
    confirmText?: string,
    cancelText?: string,
    additionalDeleteInfo?: string
  ): Promise<boolean> {
    message =
      message ??
      'Möchtest du itemName wirklich löschen? Diese Aktion kann nicht mehr rückgängig gemacht werden!';
    header = header ?? 'Löschen bestätigen';
    confirmText = confirmText ?? 'Löschen';
    cancelText = cancelText ?? 'Abbrechen';
    message = message.replace('itemName', itemName);
    message = additionalDeleteInfo
      ? message + ' ' + additionalDeleteInfo
      : message;
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header,
        message,
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
            handler: () => resolve(false),
          },
          {
            text: confirmText,
            handler: () => resolve(true),
          },
        ],
      });

      await alert.present();
    });
  }

  async presentInputAlert(
    header: string,
    message: string,
    placeholder: string = '',
    actionButton: string = 'Erstellen',
    preFilledValue: string = ''
  ): Promise<string | null> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header: header,
        message: message,
        inputs: [
          {
            name: 'name',
            type: 'text',
            placeholder: placeholder,
            value: preFilledValue,
          },
        ],
        buttons: [
          {
            text: 'Abbrechen',
            role: 'cancel',
            handler: () => resolve(null),
          },
          {
            text: actionButton,
            handler: (data) => {
              if (!data.name || data.name.trim() === '') {
                return false;
              }
              resolve(data.name.trim());
              return true;
            },
          },
        ],
      });

      await alert.present();
    });
  }

  calculateHours(timeEntries: TimeEntry[]): string {
    let allEntryMinutes = 0;
    timeEntries.forEach((x) => {
      let start = this.toTimeOnlyUtc(x.start);
      let end = this.toTimeOnlyUtc(x.end);
      const milliDiff: number = end - start;
      const totalSeconds = Math.floor(milliDiff / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      allEntryMinutes += totalMinutes;
    });
    return (
      Math.floor(allEntryMinutes / 60) + 'H ' + (allEntryMinutes % 60) + 'M'
    );
  }

  calculateDays(date1: string, date2: string): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);

    const msPerDay = 1000 * 60 * 60 * 24;
    const diffMs = Math.abs(d2.getTime() - d1.getTime());

    return Math.floor(diffMs / msPerDay) + 1;
  }

  async showLoader(message: string = null): Promise<HTMLIonLoadingElement> {
    return await this.loadingController.create({
      spinner: 'circular',
      backdropDismiss: true,
      message: message,
    });
  }

  openAppStore() {
    let appStorLink = this.platform.is('ios')
      ? 'https://apps.apple.com/de/app/baulogistik/id6747146183'
      : 'https://play.google.com/store/apps/details?id=com.schweizermedia.baulogistik';
    window.open(appStorLink, '_system');
  }

  async openMap(query: string) {
    if (this.platform.is('desktop') || this.platform.is('android')) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
        '_system'
      );
      return;
    } else {
      // iOS
      const url = `https://maps.apple.com/?q=${query}`;
      await Browser.open({ url });
    }
  }

  async showUpdateAlert() {
    let header: string = 'Veraltete App-Version';
    let message: string =
      'Ihre aktuelle App-Version wird leider nicht mehr unterstützt. Bitte aktualisieren Sie die App im App-Store!';

    const alert = await this.alertController.create({
      header,
      message,
      backdropDismiss: false,
      buttons: [
        {
          text: 'Zum App Store',
          handler: () => {
            this.openAppStore();
            return false;
          },
        },
      ],
    });

    await alert.present();
  }

  getDateString(dateString: any): string {
    let now = new Date(Date.now());
    let fileDate = new Date(dateString);
    let suffix = ', ' + this.datepipe.transform(fileDate, 'HH:mm') + ' Uhr';
    if (this.isYesterday(fileDate)) {
      return 'Gestern' + suffix;
    }
    if (this.datesAreSameDay(now, fileDate)) {
      return 'Heute' + suffix;
    }
    return this.datepipe.transform(fileDate, 'dd. MMM yyyy, HH:mm') + ' Uhr';
  }

  dateStringsAreSameDay(date1: string, date2: string): boolean {
    const dt1 = new Date(date1);
    const dt2 = new Date(date2);
    return this.datesAreSameDay(dt1, dt2);
  }

  datesAreSameDay(first: Date, second: Date) {
    return (
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate() &&
      first.getDay() === second.getDay()
    );
  }

  addLeadingZero(num: number): string {
    return num.toString().padStart(2, '0');
  }

  isYesterday(dt: Date) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return (
      dt.getDate() === yesterday.getDate() &&
      dt.getMonth() === yesterday.getMonth() &&
      dt.getFullYear() === yesterday.getFullYear()
    );
  }

  public overlapsWithDate(a: TimeEntry, b: TimeEntry): boolean {
    if (!a.start || !a.end || !b.start || !b.end) return false;

    const getDateOnly = (str: string) => {
      // Falls ISO: "2025-01-10T09:00" -> "2025-01-10"
      return str.split('T')[0];
    };

    const aDate = getDateOnly(a.start);
    const bDate = getDateOnly(b.start);

    if (!aDate || !bDate) return false;

    return aDate === bDate;
  }

  private overlaps(a: TimeEntry, b: TimeEntry): boolean {
    // ignores date
    if (!a.start || !a.end || !b.start || !b.end) return false;

    const toTimeOnly = (str: string) => {
      const time = str.split('T')[1] ?? str;
      return Date.parse('1970-01-01T' + time);
    };

    const aStart = toTimeOnly(a.start);
    const aEnd = toTimeOnly(a.end);
    const bStart = toTimeOnly(b.start);
    const bEnd = toTimeOnly(b.end);

    if ([aStart, aEnd, bStart, bEnd].some(isNaN)) return false;

    return aStart < bEnd && bStart < aEnd;
  }

  private toTimeOnlyUtc(str: string): number {
    const date = new Date(str);
    return Date.UTC(
      1970,
      0,
      1,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );
  }

  arraysOverlap(arr1: TimeEntry[], arr2: TimeEntry[]): boolean {
    // used for already booked hours for other con sites
    for (const a of arr1) {
      for (const b of arr2) {
        if (this.overlaps(a, b)) {
          return true;
        }
      }
    }
    return false;
  }

  toUtcMillis(dateString: string): number {
    return new Date(dateString).getTime(); // ISO-Strings sind UTC
  }

  hasInvalidBookedTimes(
    allowedTimes: TimeEntry[],
    bookedTimes: TimeEntry[]
  ): boolean {
    const allowed = allowedTimes
      .filter((t) => t.start && t.end)
      .map((t) => ({
        start: this.toTimeOnlyUtc(t.start!),
        end: this.toTimeOnlyUtc(t.end!),
      }));

    for (const booked of bookedTimes) {
      if (!booked.start || !booked.end) {
        continue; // oder Fehler werfen
      }

      const bookedStart = this.toTimeOnlyUtc(booked.start);
      const bookedEnd = this.toTimeOnlyUtc(booked.end);

      // Buchung muss vollständig in EINEM erlaubten Slot liegen
      const isCovered = allowed.some(
        (a) => bookedStart >= a.start && bookedEnd <= a.end
      );

      if (!isCovered) {
        return true;
      }
    }

    return false;
  }

  arrayHasOverlap(times: any[]): boolean {
    // Check if inner times overlap e.g. 8:00 - 9:00 && 8:45 - 9:45
    const sorted = [...times].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    for (let i = 0; i < sorted.length - 1; i++) {
      const currentEnd = new Date(sorted[i].end).getTime();
      const nextStart = new Date(sorted[i + 1].start).getTime();

      // Überlappung wenn end > start (Grenzfälle = erlaubt)
      if (currentEnd > nextStart) {
        return true;
      }
    }

    return false;
  }

  public getDatesBetween(
    startDateString: string,
    endDateString: string
  ): string[] {
    let startDate = new Date(startDateString);
    let endDate = new Date(endDateString);
    const start = new Date(Math.min(startDate.getTime(), endDate.getTime()));
    const end = new Date(Math.max(startDate.getTime(), endDate.getTime()));
    let dates: string[] = [];
    let count = 0;

    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current).toISOString());
      count++;
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }

  public getRoundedUtcTime(roundUp: boolean, inputDate: Date = null): string {
    const now = inputDate ?? new Date();

    let minutes = now.getUTCMinutes();
    const remainder = minutes % 15;
    let newMinutes: number;
    let offSetInHours = Math.abs(now.getTimezoneOffset() / 60);
    let utcHours = now.getUTCHours();
    if (now.getTimezoneOffset() > 0) utcHours -= Math.abs(offSetInHours);
    if (now.getTimezoneOffset() < 0) utcHours += Math.abs(offSetInHours);

    if (roundUp) {
      if (remainder === 0) {
        // Schon ein Vielfaches von 15 Minuten, auf das nächste runden
        newMinutes = minutes + 15;
      } else {
        newMinutes = minutes + (15 - remainder);
      }
      if (newMinutes >= 60) {
        utcHours += 1;
        newMinutes = 0;
      }
    } else {
      newMinutes = minutes - remainder;
    }

    const roundedDate = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        utcHours,
        newMinutes,
        0,
        0
      )
    );
    return roundedDate.toISOString();
  }

  public sendErrorToServer(error: any, username: string, version: number) {
    fetch(
      'https://bbfd-dfb7atckdjapfdbt.z03.azurefd.net/api/v0/configuration/error',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: 'HTTP-Error',
          stack: JSON.stringify(error),
          user: username,
          version: version,
        }),
      }
    );
  }

  public getMonthName(month: number): string {
    const months = [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ];

    return months[month] ?? 'Ungültiger Monat';
  }

  public blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve((reader.result as string).split(',')[1]);
      };
      reader.readAsDataURL(blob);
    });

  public hasWeekend(startDate: string, endDate?: string): boolean {
    const start = new Date(startDate);

    if (isNaN(start.getTime())) {
      throw new Error('Ungültiges Datum');
    }

    if (!endDate) {
      return [0, 6].includes(start.getDay());
    }

    const end = new Date(endDate);

    if (isNaN(end.getTime())) {
      throw new Error('Ungültiges Enddatum');
    }

    const from = start <= end ? start : end;
    const to = start <= end ? end : start;

    const day = from.getDay();
    const daysUntilSaturday = (6 - day + 7) % 7;
    const nextSaturday = new Date(from);
    nextSaturday.setDate(from.getDate() + daysUntilSaturday);

    return nextSaturday <= to;
  }
}
